// src/pages/ReturnExchange.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, RotateCcw, ArrowRightLeft, Search, Plus, Trash2, AlertTriangle, CheckCircle, Printer, X } from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const ReturnExchange = ({ invoice, companies, selectedCompanyId, products, onClose, onComplete }) => {
  const { user } = useAuth();

  const [mode, setMode] = useState('return'); // 'return' or 'exchange'
  const [returnItems, setReturnItems] = useState([]);
  const [exchangeItems, setExchangeItems] = useState([]);
  const [reason, setReason] = useState('');
  const [refundMethod, setRefundMethod] = useState('CASH');
  const [refundReference, setRefundReference] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentReference, setPaymentReference] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eligible, setEligible] = useState(true);
  const [eligibilityMessage, setEligibilityMessage] = useState('');

  // Exchange product search
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [exchangeItemForm, setExchangeItemForm] = useState({
    productId: '',
    quantity: 1,
    unitPrice: 0,
    productName: ''
  });

  const paymentMethods = ['CASH', 'PAYBILL', 'TILL'];

  // Get what customer actually paid on this invoice
  const invoicePaidAmount = parseFloat(invoice?.paidAmount || 0);
  const invoiceTotalAmount = parseFloat(invoice?.totalAmount || 0);

  useEffect(() => {
    checkEligibility();
  }, [invoice]);

  useEffect(() => {
    if (productSearch) {
      const filtered = (products || []).filter(product =>
        product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.sku?.toLowerCase().includes(productSearch.toLowerCase()) ||
        product.description?.toLowerCase().includes(productSearch.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts(products || []);
    }
  }, [productSearch, products]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.exchange-product-search')) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkEligibility = async () => {
    try {
      const result = await apiService.checkReturnEligibility(invoice.id);
      setEligible(result);
      if (!result) {
        const invoiceDate = new Date(invoice.invoiceDate);
        const today = new Date();
        const daysDiff = Math.floor((today - invoiceDate) / (1000 * 60 * 60 * 24));
        if (daysDiff > 10) {
          setEligibilityMessage(`Return window expired (${daysDiff} days since purchase). This invoice is outside the allowed return window.`);
        } else if (invoice.status === 'REFUNDED') {
          setEligibilityMessage('This invoice has already been fully refunded.');
        } else {
          setEligibilityMessage('This invoice is not eligible for return.');
        }
      }
    } catch (err) {
      setEligible(false);
      setEligibilityMessage(err.message || 'Unable to check return eligibility.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES' }).format(amount || 0);
  };

  // Toggle item for return
  const toggleReturnItem = (invoiceItem) => {
    const existing = returnItems.find(ri => ri.productId === (invoiceItem.productId || invoiceItem.product?.id));
    if (existing) {
      setReturnItems(returnItems.filter(ri => ri.productId !== existing.productId));
    } else {
      setReturnItems([...returnItems, {
        productId: invoiceItem.productId || invoiceItem.product?.id,
        productName: invoiceItem.product?.name || invoiceItem.description,
        productSku: invoiceItem.product?.sku || '',
        originalInvoiceItemId: invoiceItem.id,
        quantity: parseFloat(invoiceItem.quantity),
        maxQuantity: parseFloat(invoiceItem.quantity),
        unitPrice: parseFloat(invoiceItem.unitPrice)
      }]);
    }
  };

  const updateReturnQuantity = (productId, newQty) => {
    setReturnItems(returnItems.map(ri => {
      if (ri.productId === productId) {
        const qty = Math.min(Math.max(1, parseFloat(newQty) || 1), ri.maxQuantity);
        return { ...ri, quantity: qty };
      }
      return ri;
    }));
  };

  // Exchange item management
  const handleSelectExchangeProduct = (product) => {
    setExchangeItemForm({
      productId: product.id,
      productName: product.name,
      quantity: 1,
      unitPrice: product.price
    });
    setProductSearch(`${product.name} - ${product.sku || ''}`);
    setShowProductDropdown(false);
  };

  const handleAddExchangeItem = () => {
    const product = (products || []).find(p => p.id === parseInt(exchangeItemForm.productId));
    if (!product) return;

    const totalStock = product.quantityInStock || 0;
    const reqQty = parseFloat(exchangeItemForm.quantity);
    if (reqQty > totalStock) {
      setError(`Insufficient stock for ${product.name} [${product.sku}]. Available: ${totalStock}`);
      return;
    }

    if (exchangeItems.find(ei => ei.productId === product.id)) {
      setError(`${product.name} [${product.sku}] is already in the exchange list. Remove it first to change quantity.`);
      return;
    }

    setExchangeItems([...exchangeItems, {
      productId: product.id,
      productName: product.name,
      productSku: product.sku,
      productDescription: product.description,
      quantity: reqQty,
      unitPrice: parseFloat(exchangeItemForm.unitPrice || product.price)
    }]);

    setExchangeItemForm({ productId: '', quantity: 1, unitPrice: 0, productName: '' });
    setProductSearch('');
    setError('');
  };

  const removeExchangeItem = (productId) => {
    setExchangeItems(exchangeItems.filter(ei => ei.productId !== productId));
  };

  // ============================================================
  // CALCULATIONS - CRITICAL: Refund capped at what was paid
  // ============================================================
  const returnSubtotal = returnItems.reduce((sum, ri) => sum + (ri.unitPrice * ri.quantity), 0);
  const exchangeSubtotal = exchangeItems.reduce((sum, ei) => sum + (ei.unitPrice * ei.quantity), 0);

  // For RETURNS: actual refund = min(item value, what was paid)
  const actualRefund = Math.min(returnSubtotal, invoicePaidAmount);

  // For EXCHANGES: credit = what was paid, net = credit - new items
  // Positive net = refund to customer, negative = customer owes
  const exchangeCredit = invoicePaidAmount; // Customer's money to apply to new items
  const exchangeNet = exchangeCredit - exchangeSubtotal;

  // Combined net for display
  const netAmount = mode === 'return' ? actualRefund : exchangeNet;

  // Submit handlers
  const handleSubmitReturn = async () => {
    if (returnItems.length === 0) {
      setError('Please select at least one item to return.');
      return;
    }
    if (!reason.trim()) {
      setError('Please provide a reason for the return.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const request = {
        originalInvoiceId: invoice.id,
        companyId: parseInt(selectedCompanyId),
        returnItems: returnItems.map(ri => ({
          productId: ri.productId,
          originalInvoiceItemId: ri.originalInvoiceItemId,
          quantity: ri.quantity,
          unitPrice: ri.unitPrice
        })),
        reason,
        refundMethod,
        refundReference
      };

      const result = await apiService.processFullReturn(request);
      setSuccess(result);
      printReturnReceipt(result);
    } catch (err) {
      setError(err.message || 'Failed to process return.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitExchange = async () => {
    if (returnItems.length === 0) {
      setError('Please select at least one item to return.');
      return;
    }
    if (exchangeItems.length === 0) {
      setError('Please add at least one exchange item.');
      return;
    }
    if (!reason.trim()) {
      setError('Please provide a reason for the exchange.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const request = {
        originalInvoiceId: invoice.id,
        companyId: parseInt(selectedCompanyId),
        returnItems: returnItems.map(ri => ({
          productId: ri.productId,
          originalInvoiceItemId: ri.originalInvoiceItemId,
          quantity: ri.quantity,
          unitPrice: ri.unitPrice
        })),
        exchangeItems: exchangeItems.map(ei => ({
          productId: ei.productId,
          quantity: ei.quantity,
          unitPrice: ei.unitPrice
        })),
        reason,
        refundMethod: exchangeNet > 0 ? refundMethod : null,
        refundReference: exchangeNet > 0 ? refundReference : null,
        paymentMethod: exchangeNet < 0 ? paymentMethod : null,
        paymentReference: exchangeNet < 0 ? paymentReference : null
      };

      const result = await apiService.processExchange(request);
      setSuccess(result);
      printReturnReceipt(result);
    } catch (err) {
      setError(err.message || 'Failed to process exchange.');
    } finally {
      setLoading(false);
    }
  };

  // Thermal receipt for return/exchange
  const printReturnReceipt = (returnData) => {
    const company = (companies || []).find(c => c.id === parseInt(selectedCompanyId));
    const companyName = company?.name || 'Company';
    const companyAddress = company?.address || '';
    const companyPhone = company?.phone || '';

    const fmtAmt = (amount) => parseFloat(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const isExchange = returnData.returnType === 'EXCHANGE';
    const docTitle = isExchange ? 'EXCHANGE RECEIPT' : 'RETURN RECEIPT';

    // Use backend-calculated amounts (already capped at paidAmount)
    const refundAmt = parseFloat(returnData.refundAmount || returnData.netAmount || 0);
    const net = parseFloat(returnData.netAmount || 0);
    const paidOnInvoice = parseFloat(returnData.invoicePaidAmount || 0);
    const itemValue = parseFloat(returnData.returnSubtotal || 0);

    const receiptContent = `
      <html>
      <head>
        <title>${docTitle} - ${returnData.returnNumber}</title>
        <style>
          @page { margin: 0; size: 80mm auto; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: 'Courier New', 'Lucida Console', monospace;
            font-size: 12px; font-weight: 900;
            -webkit-print-color-adjust: exact;
            -webkit-text-stroke: 0.4px #000;
            line-height: 1.35; width: 42ch; max-width: 62mm;
            margin: 2mm auto 2mm 2mm; color: #000;
          }
          .center { text-align: center; }
          .right { text-align: right; }
          .dashed { border: none; border-top: 1px dashed #000; margin: 3px 0; }
          .solid { border: none; border-top: 2px solid #000; margin: 3px 0; }
          .company-name { font-size: 14px; font-weight: 900; letter-spacing: 1px; }
          .receipt-title { font-size: 13px; font-weight: 900; letter-spacing: 2px; padding: 2px 0; }
          .info-row { display: flex; justify-content: space-between; font-size: 11px; padding: 1px 0; }
          .items-header { display: flex; font-weight: 900; font-size: 11px; padding: 2px 0; }
          .item-row { display: flex; font-size: 11px; padding: 2px 0; }
          .col-name { flex: 1; overflow: hidden; word-wrap: break-word; padding-right: 3px; }
          .col-qty { width: 22px; text-align: center; flex-shrink: 0; }
          .col-price { width: 52px; text-align: right; flex-shrink: 0; }
          .col-total { width: 55px; text-align: right; flex-shrink: 0; }
          .summary-row { display: flex; justify-content: space-between; font-size: 11px; padding: 1px 0; }
          .total-row { display: flex; justify-content: space-between; font-size: 13px; font-weight: 900; padding: 3px 0; }
          .section-title { font-size: 11px; font-weight: 900; padding: 3px 0; letter-spacing: 1px; }
          .footer { font-size: 10px; text-align: center; padding: 2px 0; }
          .spacer { height: 4px; }
          @media print { body { width: 42ch; max-width: 62mm; margin: 2mm auto 2mm 2mm; } }
        </style>
      </head>
      <body>
        <div class="center">
          <div class="company-name">${companyName}</div>
          ${companyAddress ? `<div style="font-size:11px">${companyAddress}</div>` : ''}
          ${companyPhone ? `<div style="font-size:11px">Tel: ${companyPhone}</div>` : ''}
        </div>
        <hr class="dashed" />
        <div class="center"><div class="receipt-title">${docTitle}</div></div>
        <hr class="dashed" />

        <div class="info-row"><span>Return #:</span><span>${returnData.returnNumber}</span></div>
        <div class="info-row"><span>Original Invoice:</span><span>${returnData.originalInvoiceNumber}</span></div>
        <div class="info-row"><span>Date:</span><span>${new Date().toLocaleDateString('en-GB')} ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span></div>
        <div class="info-row"><span>Customer:</span><span>${returnData.customerName || '-'}</span></div>
        <div class="info-row"><span>Processed by:</span><span>${returnData.processedBy || '-'}</span></div>

        <hr class="solid" />

        <!-- Returned Items -->
        <div class="section-title">ITEMS RETURNED</div>
        <div class="items-header">
          <span class="col-name">Item</span>
          <span class="col-qty">Qty</span>
          <span class="col-price">Price</span>
          <span class="col-total">Total</span>
        </div>
        <hr class="dashed" />
        ${(returnData.returnedItems || []).map(item => `
          <div class="item-row">
            <span class="col-name">${item.productName || '-'}${item.productDescription ? `<br/><span style="font-size:10px;font-weight:700;">${item.productDescription}</span>` : ''}</span>
            <span class="col-qty">${parseFloat(item.quantity).toFixed(0)}</span>
            <span class="col-price">${fmtAmt(item.unitPrice)}</span>
            <span class="col-total">${fmtAmt(item.lineTotal)}</span>
          </div>
        `).join('')}

        <hr class="dashed" />
        <div class="summary-row"><span>Item Value:</span><span>Ksh ${fmtAmt(itemValue)}</span></div>
        <div class="summary-row"><span>Paid on Invoice:</span><span>Ksh ${fmtAmt(paidOnInvoice)}</span></div>

        ${isExchange ? `
          <hr class="solid" />
          <div class="section-title">NEW ITEMS (EXCHANGE)</div>
          <div class="items-header">
            <span class="col-name">Item</span>
            <span class="col-qty">Qty</span>
            <span class="col-price">Price</span>
            <span class="col-total">Total</span>
          </div>
          <hr class="dashed" />
          ${(returnData.exchangedItems || []).map(item => `
            <div class="item-row">
              <span class="col-name">${item.productName || '-'}${item.productDescription ? `<br/><span style="font-size:10px;font-weight:700;">${item.productDescription}</span>` : ''}</span>
              <span class="col-qty">${parseFloat(item.quantity).toFixed(0)}</span>
              <span class="col-price">${fmtAmt(item.unitPrice)}</span>
              <span class="col-total">${fmtAmt(item.lineTotal)}</span>
            </div>
          `).join('')}
          <hr class="dashed" />
          <div class="summary-row"><span>New Items Value:</span><span>Ksh ${fmtAmt(returnData.exchangeSubtotal)}</span></div>
          ${returnData.exchangeInvoiceNumber ? `
            <div class="summary-row"><span>New Invoice:</span><span>${returnData.exchangeInvoiceNumber}</span></div>
          ` : ''}
        ` : ''}

        <hr class="solid" />

        ${net > 0 ? `
          <div class="total-row"><span>REFUND TO CUSTOMER:</span><span>Ksh ${fmtAmt(net)}</span></div>
          <div class="summary-row"><span>Refund Method:</span><span>${returnData.refundMethod || '-'}</span></div>
          ${returnData.refundReference ? `<div class="summary-row"><span>Ref:</span><span>${returnData.refundReference}</span></div>` : ''}
        ` : net < 0 ? `
          <div class="total-row"><span>CUSTOMER TOP-UP:</span><span>Ksh ${fmtAmt(Math.abs(net))}</span></div>
        ` : `
          <div class="total-row"><span>EVEN EXCHANGE</span><span>Ksh 0.00</span></div>
        `}

        ${returnData.reason ? `
          <hr class="dashed" />
          <div class="summary-row"><span>Reason:</span><span>${returnData.reason}</span></div>
        ` : ''}

        <hr class="dashed" />
        <div class="footer">
          <div>Returns accepted within 10 days</div>
          <div class="spacer"></div>
          <div>Printed: ${new Date().toLocaleString('en-GB')}</div>
        </div>
        <div class="spacer"></div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank', 'width=300,height=600');
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  // ===== SUCCESS VIEW =====
  if (success) {
    const successNet = parseFloat(success.netAmount || 0);
    const successRefund = parseFloat(success.refundAmount || Math.max(0, successNet));
    const successPaid = parseFloat(success.invoicePaidAmount || 0);
    return (
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={onClose} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
            {success.returnType === 'EXCHANGE' ? 'Exchange' : 'Return'} Completed
          </h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="text-center mb-6">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-3" />
            <h2 className="text-2xl font-bold text-gray-900">{success.returnNumber}</h2>
            <p className="text-gray-600">
              {success.returnType === 'EXCHANGE' ? 'Exchange' : 'Return'} processed successfully
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <div className="text-sm text-gray-600">Item Value (Returned)</div>
              <div className="text-xl font-bold text-blue-600">{formatCurrency(success.returnSubtotal)}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <div className="text-sm text-gray-600">Amount Paid on Invoice</div>
              <div className="text-xl font-bold text-gray-700">{formatCurrency(successPaid)}</div>
            </div>
            {success.returnType === 'EXCHANGE' && (
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-sm text-gray-600">New Items Value</div>
                <div className="text-xl font-bold text-orange-600">{formatCurrency(success.exchangeSubtotal)}</div>
              </div>
            )}
            <div className={`rounded-lg p-4 text-center ${successNet > 0 ? 'bg-green-50' : successNet < 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <div className="text-sm text-gray-600">
                {successNet > 0 ? 'Refund to Customer' : successNet < 0 ? 'Customer Top-Up' : 'Even Exchange'}
              </div>
              <div className={`text-xl font-bold ${successNet > 0 ? 'text-green-600' : successNet < 0 ? 'text-red-600' : 'text-gray-600'}`}>
                {formatCurrency(Math.abs(successNet))}
              </div>
            </div>
          </div>

          {/* Explanation for partial payment returns */}
          {success.returnType === 'FULL_RETURN' && successPaid < parseFloat(success.returnSubtotal) && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="text-sm text-blue-800">
                <strong>Note:</strong> The item was worth {formatCurrency(success.returnSubtotal)} but only {formatCurrency(successPaid)} was
                paid. The refund is {formatCurrency(successRefund)} (amount actually paid). The
                remaining balance of {formatCurrency(parseFloat(success.returnSubtotal) - successPaid)} has been forgiven.
              </div>
            </div>
          )}

          {success.exchangeInvoiceNumber && (
            <div className="bg-indigo-50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-600">New Exchange Invoice</div>
              <div className="font-bold text-indigo-600">{success.exchangeInvoiceNumber}</div>
              <div className="text-xs text-gray-500">Referencing: {success.originalInvoiceNumber}</div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={() => printReturnReceipt(success)}
              className="flex-1 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center space-x-2"
            >
              <Printer className="w-4 h-4" />
              <span>Reprint Receipt</span>
            </button>
            <button
              onClick={() => { if (onComplete) onComplete(); onClose(); }}
              className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== NOT ELIGIBLE =====
  if (!eligible) {
    return (
      <div className="space-y-4 lg:space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={onClose} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Return / Exchange</h1>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center mb-2">
            <AlertTriangle className="w-6 h-6 text-red-500 mr-2" />
            <h3 className="text-lg font-medium text-red-800">Not Eligible</h3>
          </div>
          <p className="text-red-700">{eligibilityMessage}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // ===== MAIN FORM =====
  return (
    <div className="space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button onClick={onClose} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">
            {mode === 'return' ? 'Process Return' : 'Process Exchange'}
          </h1>
          <p className="text-gray-600 text-sm">Invoice: {invoice?.invoiceNumber}</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Mode Toggle */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="flex space-x-2">
          <button
            onClick={() => { setMode('return'); setExchangeItems([]); }}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2
              ${mode === 'return' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <RotateCcw className="w-4 h-4" />
            <span>Full Return</span>
          </button>
          <button
            onClick={() => setMode('exchange')}
            className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2
              ${mode === 'exchange' ? 'bg-orange-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <ArrowRightLeft className="w-4 h-4" />
            <span>Exchange</span>
          </button>
        </div>
      </div>

      {/* Invoice Payment Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-xs text-gray-500 uppercase">Invoice Total</div>
            <div className="text-lg font-bold text-gray-800">{formatCurrency(invoiceTotalAmount)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Amount Paid</div>
            <div className="text-lg font-bold text-green-600">{formatCurrency(invoicePaidAmount)}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500 uppercase">Outstanding Balance</div>
            <div className="text-lg font-bold text-red-600">{formatCurrency(invoice?.balanceAmount)}</div>
          </div>
        </div>
        {invoicePaidAmount === 0 && mode === 'return' && (
          <div className="mt-2 text-xs text-blue-700 text-center">
            No payment was made on this invoice. The return will cancel the invoice with zero refund.
          </div>
        )}
        {invoicePaidAmount === 0 && mode === 'exchange' && (
          <div className="mt-2 text-xs text-blue-700 text-center">
            No payment was made on this invoice. The exchange will transfer the outstanding balance to the new invoice.
          </div>
        )}
        {invoicePaidAmount > 0 && invoicePaidAmount < invoiceTotalAmount && (
          <div className="mt-2 text-xs text-blue-700 text-center">
            This invoice was partially paid. {mode === 'return'
              ? `Refund will be based on the amount actually paid (${formatCurrency(invoicePaidAmount)}), not the item value.`
              : `The paid amount (${formatCurrency(invoicePaidAmount)}) will be credited to the new invoice.`}
          </div>
        )}
      </div>

      {/* Step 1: Select Items to Return */}
      <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
        <h3 className="font-medium text-gray-900 mb-3">Step 1: Select Items to Return</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-10">Select</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Qty Sold</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Return Qty</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Price</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Return Value</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(invoice?.items || []).map((item, index) => {
                const isSelected = returnItems.some(ri => ri.productId === (item.productId || item.product?.id));
                const returnItem = returnItems.find(ri => ri.productId === (item.productId || item.product?.id));
                return (
                  <tr key={index} className={isSelected ? 'bg-red-50' : ''}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleReturnItem(item)}
                        className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">
                      {item.product?.name || item.description}
                      {item.product?.sku && (
                        <span className="text-gray-500 text-xs ml-1">- {item.product.sku}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">{item.quantity}</td>
                    <td className="px-3 py-2">
                      {isSelected ? (
                        <input
                          type="number"
                          min={1}
                          max={item.quantity}
                          value={returnItem?.quantity || 1}
                          onChange={(e) => updateReturnQuantity(item.productId || item.product?.id, e.target.value)}
                          className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                        />
                      ) : '-'}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                    <td className="px-3 py-2 text-sm font-medium text-red-600">
                      {isSelected ? formatCurrency((returnItem?.unitPrice || 0) * (returnItem?.quantity || 0)) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {returnItems.length > 0 && (
          <div className="mt-3 text-right">
            <span className="text-sm text-gray-600">Item Value: </span>
            <span className="text-lg font-bold text-red-600">{formatCurrency(returnSubtotal)}</span>
          </div>
        )}
      </div>

      {/* Step 2 (Exchange only): Select New Items */}
      {mode === 'exchange' && (
        <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
          <h3 className="font-medium text-gray-900 mb-3">Step 2: Select New Items (Exchange For)</h3>

          {/* Product Search */}
          <div className="relative mb-4 exchange-product-search">
            <div className="flex space-x-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                  onFocus={() => setShowProductDropdown(true)}
                  placeholder="Search products..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 text-sm"
                />
                {showProductDropdown && filteredProducts.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {filteredProducts.slice(0, 15).map(product => {
                      const stock = product.quantityInStock || 0;
                      const outOfStock = stock <= 0;
                      return (
                        <div
                          key={product.id}
                          onClick={() => !outOfStock && handleSelectExchangeProduct(product)}
                          className={`px-3 py-2 text-sm ${outOfStock
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'hover:bg-orange-50 cursor-pointer'}`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <span className="font-medium">{product.name}</span>
                              <span className="text-gray-500 ml-1">- {product.sku}</span>
                              {product.description && (
                                <span className="text-gray-400 ml-1 text-xs">({product.description})</span>
                              )}
                            </div>
                            <div className="text-right flex-shrink-0 ml-2">
                              <span className="text-orange-600">{formatCurrency(product.price)}</span>
                              <span className={`ml-2 text-xs ${outOfStock ? 'text-red-500 font-bold' : 'text-green-600'}`}>
                                {outOfStock ? 'OUT' : `Qty: ${stock}`}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <input
                type="number"
                min={1}
                value={exchangeItemForm.quantity}
                onChange={(e) => setExchangeItemForm({ ...exchangeItemForm, quantity: parseInt(e.target.value) || 1 })}
                className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm text-center"
                placeholder="Qty"
              />
              <button
                onClick={handleAddExchangeItem}
                disabled={!exchangeItemForm.productId}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-300 text-sm"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Exchange items list */}
          {exchangeItems.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-orange-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">New Item</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Price</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Total</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {exchangeItems.map((item, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2 text-sm text-gray-900">
                        {item.productName}
                        {item.productSku && (
                          <span className="text-gray-500 text-xs ml-1">- {item.productSku}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900">{item.quantity}</td>
                      <td className="px-3 py-2 text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">{formatCurrency(item.unitPrice * item.quantity)}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeExchangeItem(item.productId)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {exchangeItems.length > 0 && (
            <div className="mt-3 text-right">
              <span className="text-sm text-gray-600">Exchange Subtotal: </span>
              <span className="text-lg font-bold text-orange-600">{formatCurrency(exchangeSubtotal)}</span>
            </div>
          )}
        </div>
      )}

      {/* Financial Summary & Reason */}
      {returnItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border p-4 lg:p-6">
          <h3 className="font-medium text-gray-900 mb-3">
            {mode === 'exchange' ? 'Step 3: Review & Complete' : 'Step 2: Review & Complete'}
          </h3>

          {/* Net Amount Summary */}
          <div className={`rounded-lg p-4 mb-4 ${
            mode === 'return' ? 'bg-green-50 border border-green-200' :
            exchangeNet > 0 ? 'bg-green-50 border border-green-200' :
            exchangeNet < 0 ? 'bg-red-50 border border-red-200' :
            'bg-gray-50 border border-gray-200'
          }`}>
            {mode === 'return' ? (
              /* RETURN MODE: Show item value, amount paid, actual refund */
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Item Value</div>
                    <div className="text-lg font-bold text-blue-600">{formatCurrency(returnSubtotal)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Amount Paid on Invoice</div>
                    <div className="text-lg font-bold text-gray-700">{formatCurrency(invoicePaidAmount)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Actual Refund</div>
                    <div className="text-xl font-bold text-green-600">{formatCurrency(actualRefund)}</div>
                  </div>
                </div>
                {invoicePaidAmount === 0 && (
                  <div className="text-xs text-center text-blue-700 bg-blue-50 rounded p-2">
                    No payment was made on this invoice. The return will cancel the sale with zero refund.
                  </div>
                )}
                {invoicePaidAmount > 0 && invoicePaidAmount < returnSubtotal && (
                  <div className="text-xs text-center text-blue-700 bg-blue-50 rounded p-2">
                    Refund is {formatCurrency(actualRefund)} (what was paid), not {formatCurrency(returnSubtotal)} (item value).
                    Balance of {formatCurrency(returnSubtotal - invoicePaidAmount)} is forgiven.
                  </div>
                )}
              </div>
            ) : (
              /* EXCHANGE MODE: Show credit, new items, net */
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xs text-gray-500 uppercase">Credit (Paid on Old Invoice)</div>
                    <div className="text-lg font-bold text-green-600">{formatCurrency(exchangeCredit)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">New Items</div>
                    <div className="text-lg font-bold text-orange-600">{formatCurrency(exchangeSubtotal)}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 uppercase">
                      {exchangeNet > 0 ? 'Refund to Customer' :
                       exchangeNet < 0 ? (invoicePaidAmount === 0 ? 'Balance on New Invoice' : 'Customer Top-Up') :
                       'Even Exchange'}
                    </div>
                    <div className={`text-xl font-bold ${
                      exchangeNet > 0 ? 'text-green-600' : exchangeNet < 0 ? 'text-red-600' : 'text-gray-600'
                    }`}>
                      {formatCurrency(Math.abs(exchangeNet))}
                    </div>
                  </div>
                </div>
                {invoicePaidAmount === 0 && exchangeSubtotal === returnSubtotal && (
                  <div className="text-xs text-center text-blue-700 bg-blue-50 rounded p-2">
                    Same-price exchange with no prior payment. The outstanding balance will carry over to the new invoice.
                  </div>
                )}
                {invoicePaidAmount === 0 && exchangeSubtotal !== returnSubtotal && (
                  <div className="text-xs text-center text-blue-700 bg-blue-50 rounded p-2">
                    No payment was made. The new invoice will have a balance of {formatCurrency(exchangeSubtotal)}.
                  </div>
                )}
                {invoicePaidAmount > 0 && invoicePaidAmount < returnSubtotal && (
                  <div className="text-xs text-center text-blue-700 bg-blue-50 rounded p-2">
                    Credit is based on amount paid ({formatCurrency(invoicePaidAmount)}), not item value ({formatCurrency(returnSubtotal)}).
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for {mode === 'return' ? 'Return' : 'Exchange'} *</label>
            <textarea
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Wrong size, Defective item, Customer changed mind..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
            />
          </div>

          {/* Refund Method (for returns, or exchange with positive net) */}
          {(mode === 'return' || exchangeNet > 0) && actualRefund > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refund Method *</label>
                <select
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                >
                  {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Refund Reference</label>
                <input
                  type="text"
                  value={refundReference}
                  onChange={(e) => setRefundReference(e.target.value)}
                  placeholder="Transaction ID, M-Pesa code, etc."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
            </div>
          )}

          {/* Payment Method (for exchange where customer owes) */}
          {mode === 'exchange' && exchangeNet < 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Payment Method</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                >
                  {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reference</label>
                <input
                  type="text"
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder="Transaction ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                />
              </div>
            </div>
          )}

          {/* Admin-only notice */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <div className="flex items-center text-yellow-700 text-sm">
              <AlertTriangle className="w-4 h-4 mr-2" />
              This action can only be performed by Admin users and cannot be undone.
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={mode === 'return' ? handleSubmitReturn : handleSubmitExchange}
              disabled={loading || returnItems.length === 0 || (mode === 'exchange' && exchangeItems.length === 0)}
              className={`flex-1 px-6 py-3 text-white rounded-lg transition-colors flex items-center justify-center space-x-2
                ${mode === 'return'
                  ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-300'
                  : 'bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300'
                } disabled:cursor-not-allowed`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  {mode === 'return' ? <RotateCcw className="w-4 h-4" /> : <ArrowRightLeft className="w-4 h-4" />}
                  <span>{mode === 'return' ? 'Process Return & Refund' : 'Process Exchange'}</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReturnExchange;